<template>
  <div class="width-100">
    <h2 class="section-title">{{ $t('signatureConfig.title') }}</h2>

    <div class="form-block signature-config-form">
      <ValidationObserver
        ref="signatureProfileForm"
        v-slot="{ handleSubmit, invalid }"
      >
        <el-form
          ref="signatureConfigForm"
          label-position="top"
          size="small"
        >
          <el-row :gutter="20">
            <el-col :span="6">
              <FormItem :label="$t('signatureConfig.status')">
                <el-tag
                  v-if="signatureData"
                  :class="[
                    `signature-status--is-${
                      signatureData.enabled ? 'active' : 'waiting'
                    }`,
                  ]"
                  class="signature-status"
                >
                  {{
                    $t(
                      `constants.${
                        signatureData.enabled ? 'active' : 'waiting'
                      }`
                    )
                  }}
                </el-tag>
                <el-tag
                  v-else
                  class="signature-status signature-status--is-undefined"
                >
                  ND
                </el-tag>
              </FormItem>
            </el-col>
            <el-col :span="6">
              <FormItem :label="$t('signatureConfig.activationDate')">
                <label
                  v-if="signatureData && signatureData.activatedAt"
                  class="signature-date"
                >
                  {{ signatureData.activatedAt | date }}
                </label>
                <label
                  class="signature-date signature-date--undefined"
                  v-else
                  >-</label
                >
              </FormItem>
            </el-col>
            <el-col :span="6">
              <FormItem :label="$t('signatureConfig.expirationDate')">
                <label
                  v-if="signatureData && signatureData.certificateExpiration"
                  class="signature-date"
                >
                  {{ signatureData.certificateExpiration | date }}
                </label>
                <label
                  class="signature-date signature-date--undefined"
                  v-else
                  >-</label
                >
              </FormItem>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :span="6">
              <FormItem
                :label="$t('signatureConfig.user')"
                rules="required"
              >
                <el-input v-model="signatureData.signatureAccountId" />
              </FormItem>
            </el-col>
            <el-col :span="4">
              <FormItem
                :label="$t('signatureConfig.password')"
                rules="required"
              >
                <el-input
                  show-password
                  v-model="signatureData.signaturePassword"
                />
              </FormItem>
            </el-col>
            <el-col :span="6">
              <FormItem :label="$t('signatureConfig.displayTitle')">
                <el-input v-model="signatureData.displayTitle" />
              </FormItem>
            </el-col>
          </el-row>

          <!-- Hidden Section -->
          <el-row
            :gutter="20"
            v-if="true === false"
          >
            <el-col :span="6">
              <FormItem :label="$t('signatureConfig.name')">
                <el-input v-model="signatureName" />
              </FormItem>
            </el-col>
          </el-row>
          <!-- End Hidden Section -->

          <!-- Hidden section -->
          <el-row
            :gutter="20"
            v-if="true === false"
          >
            <el-col :span="6">
              <FormItem :label="$t('signatureConfig.image')">
                <el-upload
                  ref="upload"
                  :class="{ 'signature-uploader--hidden': fileList.length }"
                  class="signature-uploader"
                  action=""
                  :limit="1"
                  :show-file-list="true"
                  :auto-upload="false"
                  :multiple="false"
                  accept=".jpeg,.jpg"
                  :file-list="fileList"
                  :on-change="handleChange"
                  :on-remove="handleRemove"
                >
                  <img
                    v-if="imageUrl"
                    :src="imageUrl"
                    class="signature-uploader__signature"
                  />
                  <i
                    v-else
                    class="el-icon-plus signature-uploader__icon"
                  ></i>
                </el-upload>
              </FormItem>
            </el-col>
          </el-row>
          <!-- End Hidden Section -->

          <FormButtons>
            <el-button
              type="primary"
              native-type="submit"
              :disabled="invalid"
              @click.prevent="handleSubmit(updateSignature)"
            >
              {{ $t('signatureConfig.save') }}
            </el-button>
          </FormButtons>
        </el-form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';

  import FormItem from '@/components/FormItem.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import { signatureApi } from '@/api';
  import { SignatureStatus } from '@/config/constants';

  const formData = {
    enabled: false,
    activatedAt: null,
    certificateExpiration: null,
    signatureAccountId: '',
    signaturePassword: '',
    displayTitle: null,
  };

  export default {
    name: 'SignatureConfig',
    components: {
      FormItem,
      FormButtons,
    },
    data() {
      return {
        imageUrl: null,
        file: null,
        fileList: [],
        signatureName: null,
        signatureData: { ...formData },
        signatureStatus: SignatureStatus,
      };
    },
    computed: {
      ...mapGetters(['loggedUser']),
      signatureProfileExists() {
        return !!this.loggedUser.signature;
      },
    },
    created() {
      // if (this.loggedUser.signature) {
      //   this.fileList = [{ name: this.loggedUser.signature.filename }];
      //   try {
      //     const response = await signatureApi.retrieveSignature(this.loggedUser.signatureId);
      //     this.signatureData = response.data;
      //   } catch (error) {
      //     this.$notify({ showClose: true, type: 'error', message: error.response.data.error.message });
      //   }
      // }
      // if (this.loggedUser.signatureName) {
      //   this.signatureName = this.loggedUser.signatureName.slice(0);
      // }
      if (this.signatureProfileExists) {
        this.getSignature();
      }
    },
    methods: {
      ...mapActions(['setLoggedUser']),
      handleChange(file) {
        const isJPG = file.raw.type === 'image/jpeg';
        const isLt1M = file.size / 1024 / 1024 < 1;
        if (!isJPG) {
          this.$message.error(this.$t('signatureConfig.notJpg'));
          this.$refs.upload.clearFiles();
        }
        if (!isLt1M) {
          this.$message.error(this.$t('signatureConfig.sizeExceeded'));
          this.$refs.upload.clearFiles();
        }
        this.file = isJPG && isLt1M ? file : null;
        if (this.file) {
          this.imageUrl = URL.createObjectURL(this.file.raw);
        }
        return isJPG && isLt1M;
      },
      handleRemove() {
        this.file = null;
        this.imageUrl = null;
        this.fileList = [];
      },
      async getSignature() {
        try {
          const signatureProfile = await signatureApi.retrieveSignature();
          const {
            enabled,
            activatedAt,
            certificateExpiration,
            signatureAccountId,
            displayTitle,
          } = signatureProfile.data;
          this.signatureData = {
            enabled,
            activatedAt,
            certificateExpiration,
            signatureAccountId,
            displayTitle,
          };
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async updateSignature() {
        // try {
        //   let signatureId;
        //   if (this.file) {
        //     const stream = await this.file.raw.arrayBuffer();
        //     const response = await signatureApi.uploadSignature(stream, this.file.name);
        //     signatureId = response.data.id;
        //   }
        //   const data = {
        //     signatureName: this.signatureName,
        //   };
        //   if (signatureId) data.signatureId = signatureId;

        //   await usersApi.patchMe(data);
        //   const userInfo = await usersApi.getInfo();
        //   this.setLoggedUser(userInfo.data);

        //   this.$notify({ showClose: true, type: 'success', message: this.$t('signatureConfig.saveSuccess') });
        // } catch (error) {
        //   this.$notify({ showClose: true, type: 'error', message: error.response.data.error.message });
        // }
        try {
          const { signatureAccountId, signaturePassword, displayTitle } =
            this.signatureData;
          if (this.signatureProfileExists) {
            await signatureApi.updateSignature({
              signatureAccountId,
              signaturePassword,
              displayTitle,
            });
          } else {
            await signatureApi.createSignature({
              signatureAccountId,
              signaturePassword,
              displayTitle,
            });
          }
          await this.getSignature();
          this.$refs.signatureProfileForm.reset();
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('signatureConfig.saveSuccess'),
          });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
    },
  };
</script>

<style lang="scss">
  .signature-uploader {
    .el-upload {
      border: 1px dashed $--color-white;
      border-radius: 5px;
      cursor: pointer;
      position: relative;
      overflow: hidden;
    }

    // &.el-upload:hover {
    //   border-color: #409EFF;
    // }

    &__icon {
      font-size: 28px;
      color: $--color-white;
      width: 178px;
      height: 178px;
      line-height: 178px;
      text-align: center;
    }

    &__signature {
      width: 100%;
      height: auto;
      display: block;
    }

    &--hidden {
      .el-upload {
        display: none;
      }
    }
  }
  .signature-status {
    &--is-active {
      color: $--color-white;
      background: $--color-success;
      border-color: $--color-success;
    }
    &--is-waiting {
      color: $--color-white;
      background: $--color-warning;
      border-color: $--color-warning;
    }
    &--is-expired {
      color: $--color-white;
      background: $--color-danger;
      border-color: $--color-danger;
    }
    &--is-undefined {
      color: $--color-white;
      background: $--color-text-regular;
      border-color: $--color-text-regular;
    }
  }

  .signature-date {
    color: $--color-white;

    &--undefined {
      color: $--color-text-regular;
    }
  }
</style>
